import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { SignalOccurrencesAPI } from 'api/signal-occurrences';
import {
  SignalSummaryRequest,
  SignalSummaryResponse,
  SignalSummaryValueApi,
} from 'api/signal-occurrences/types';
import { SignalSource, Timeline } from 'api/timeline/types';

const SIGNAL_SUMMARY_QUERY_KEY = (
  companyId: number,
  payload: SignalSummaryRequest,
) => ['signal-occurrences', 'summary', companyId, payload];

const EMAIL_TIMELINE_QUERY_KEY = (companyId: number, sourceId: number) =>
  ['signal-occurrences', 'email-timeline', companyId, sourceId] as const;

export const useEmailTimelineQuery = (
  companyId: number,
  sourceId: number | undefined,
) => {
  return useQuery<Timeline | null>({
    queryKey: EMAIL_TIMELINE_QUERY_KEY(companyId, sourceId ?? -1),
    queryFn: async () => {
      const response = await SignalOccurrencesAPI.getEmailTimeline(companyId, {
        sourceType: 'EMAIL',
        sourceId: String(sourceId),
        view: 'MEETINGS&EMAILS',
      });

      for (const timelines of Object.values(response)) {
        const match = timelines.find(
          (t) =>
            t.source.type === SignalSource.EMAIL &&
            Number(t.source.id) === sourceId,
        );

        if (match) {
          return match;
        }
      }

      return null;
    },
    enabled: companyId > 0 && sourceId != null && sourceId > 0,
  });
};

export const useGetSignalSummaryQuery = (
  companyId: number,
  payload: SignalSummaryRequest,
  queryOptions?: Omit<
    UseQueryOptions<SignalSummaryResponse, unknown, SignalSummaryResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: SIGNAL_SUMMARY_QUERY_KEY(companyId, payload),
    queryFn: async () => {
      const response = await SignalOccurrencesAPI.getSummary(
        companyId,
        payload,
      );

      // Ensure returned object matches SignalSummaryResponse type (property name correction)
      return {
        groupBy: response.groupBy,
        values: response.values as unknown as SignalSummaryValueApi[],
      } as SignalSummaryResponse;
    },
    ...queryOptions,
  });
};
